import type { PolicyArtefactType } from '@/lib/apiTypes'

/**
 * Starting points for the policy editor.
 *
 * Templates are artefact-specific: each set only reads fields that exist on THAT artefact's
 * policy input (module.*, state.*, provider.*, plan.*) so an inserted template evaluates as-is.
 * All Rego v1 ("deny contains msg if"). Trust-signal gates guard null and honour the org
 * signal-fail gate, mirroring the built-in default policies.
 *
 * Three rules every entry here obeys, each of them load-bearing and each pinned by a test in
 * regoTemplates.test.ts:
 *
 *  1. "package terramantle.policy" with a "deny contains msg if" rule. The scanners query
 *     data.terramantle.policy.deny and the bundle builder refuses to relocate anything else, so
 *     a policy in another package is stored and then never fires. The API rejects it on write.
 *  2. Real OPA parses it. Rego v1 rejects plenty that reads fine.
 *  3. It is EVALUATED against a real contract-v2 input before it ships - once on a document that
 *     should trip it and once on a clean one. Parsing proves nothing about whether a rule can
 *     ever match: scan.findings.critical and scan.vulnerabilities.critical sit one dot apart and
 *     count different things, and reaching for the wrong one reads correctly.
 *
 * Two traps worth knowing before editing these:
 *
 *  - "not f.meta.fixed_version" does NOT catch "fixed_version": null, because null is defined
 *    and is not false. Every "this signal is absent" test below compares to null explicitly.
 *  - No Rego body here contains a backtick, because these are TypeScript template literals.
 *    Where a raw string would be reached for, the rule is written without one.
 */
export type Template = {
  label: string
  description: string
  rego: string
  /**
   * Curated "most orgs start here", rendered as a star in the gallery. NOT a user preference and
   * never persisted. "true" only, never false: absence is the negative, so there is exactly one
   * representation of "not popular".
   */
  popular?: true
  /**
   * In the recommended baseline for this artefact - the set the "Add recommended" dialog offers
   * and ticks by default. Deliberately a SEPARATE flag from `popular`: that one orders the
   * gallery, and a set that reads well as a starting baseline (3-5, non-overlapping, covering
   * the obvious ground) is not the same question as which single card to star. Widening one
   * should never silently reorganise the other. Same "true" only rule.
   */
  baseline?: true
}

export const TEMPLATES: Record<PolicyArtefactType, Template[]> = {
  module: [
    {
      label: 'No critical findings',
      description: 'Block any module with critical severity findings',
      rego: `package terramantle.policy

deny contains msg if {
  input.scan.findings.critical > 0
  msg := sprintf("Module has %d critical finding(s)", [input.scan.findings.critical])
}`,
    },
    {
      label: 'No critical or high findings',
      description: 'Block modules with critical or high severity findings',
      baseline: true,
      popular: true,
      rego: `package terramantle.policy

deny contains msg if {
  input.scan.findings.critical > 0
  msg := sprintf("Module has %d critical finding(s)", [input.scan.findings.critical])
}

deny contains msg if {
  input.scan.findings.high > 0
  msg := sprintf("Module has %d high finding(s)", [input.scan.findings.high])
}`,
    },
    {
      label: 'Risk budget',
      description: 'Score a module by severity and block it when the total exceeds the budget you set',
      rego: `package terramantle.policy

# What each severity costs, and the total a module may carry. Both are yours to tune: this is
# the shape of a risk acceptance, not a line anyone else can draw for you.
weights := {"critical": 10, "high": 5, "medium": 2, "low": 1}
budget := 20

score := sum([n |
  some severity, weight in weights
  n := input.scan.findings[severity] * weight
])

deny contains msg if {
  score > budget
  msg := sprintf("Module risk score %d exceeds the budget of %d (%d critical, %d high, %d medium, %d low)", [score, budget, input.scan.findings.critical, input.scan.findings.high, input.scan.findings.medium, input.scan.findings.low])
}`,
    },
    {
      label: 'No secrets in module source',
      description: 'Block modules carrying a credential in their source, and say so differently when the scanner proved it is live',
      baseline: true,
      rego: `package terramantle.policy

# A VERIFIED secret is one the scanner used successfully - a working key, not a string that
# looks like one. That is categorically worse, so the two get their own messages.
_verified(f) if { f.meta.verified == true }

deny contains msg if {
  some f in input.findings
  f.category == "secret"
  _verified(f)
  msg := sprintf("LIVE credential committed to the module source: %s", [f.title])
}

deny contains msg if {
  some f in input.findings
  f.category == "secret"
  not _verified(f)
  msg := sprintf("Suspected credential in the module source: %s", [f.title])
}`,
    },
    {
      label: 'No publicly exposed resources',
      description: 'Block modules that expose resources to the internet (open security groups, public S3, etc.)',
      baseline: true,
      rego: `package terramantle.policy

public_exposure_rules := {
  "AVD-AWS-0107", # Security group open to internet
  "AVD-AWS-0086", # S3 bucket publicly accessible
  "AVD-AWS-0173", # RDS publicly accessible
}

deny contains msg if {
  some finding in input.findings
  finding.rule_id in public_exposure_rules
  msg := sprintf("Public exposure detected: %s (%s)", [finding.title, finding.rule_id])
}`,
    },
    {
      label: 'Require encryption at rest',
      description: 'Block modules where encryption at rest is not enabled',
      rego: `package terramantle.policy

encryption_rules := {
  "AVD-AWS-0080", # EBS encryption
  "AVD-AWS-0028", # RDS storage encryption
  "AVD-AWS-0132", # S3 bucket encryption
  "AVD-AWS-0023", # Cloudtrail encryption
}

deny contains msg if {
  some finding in input.findings
  finding.rule_id in encryption_rules
  msg := sprintf("Encryption not enabled: %s (%s)", [finding.title, finding.rule_id])
}`,
    },
    {
      label: 'No sensitive variable defaults',
      description: 'Block modules that ship a default value on a variable marked sensitive',
      baseline: true,
      popular: true,
      rego: `package terramantle.policy

deny contains msg if {
  some v in input.variables
  v.sensitive == true
  v.has_default == true
  msg := sprintf("Sensitive variable '%s' declares a default - the value ships with the module", [v.name])
}`,
    },
    {
      label: 'Every variable documented',
      description: 'Block modules with an undocumented input variable - a consumer cannot tell what to pass',
      rego: `package terramantle.policy

deny contains msg if {
  some v in input.variables
  _undocumented(v)
  msg := sprintf("Variable '%s' has no description", [v.name])
}

_undocumented(v) if { v.description == null }
_undocumented(v) if { v.description == "" }`,
    },
    {
      label: 'Pin every provider',
      description: 'Block modules whose required_providers leave a version unpinned',
      baseline: true,
      popular: true,
      rego: `package terramantle.policy

deny contains msg if {
  some p in input.required_providers
  p.version_constraint in {null, ""}
  msg := sprintf("Provider %s/%s is not version-pinned", [p.namespace, p.type])
}`,
    },
    {
      label: 'Bound every provider constraint',
      description: 'Block an open-ended constraint like >= 5.0, which lets an unreviewed major version arrive on its own',
      rego: `package terramantle.policy

# A constraint that opens with > and never closes has no ceiling, so a major release nobody
# reviewed can land in a build that did not change. A pessimistic (~>) or exact constraint, or
# one carrying its own upper bound (">= 5.0, < 6.0"), is fine. A MISSING constraint is a
# different template ("Pin every provider"); this one is about the constraints that exist.
deny contains msg if {
  some p in input.required_providers
  c := p.version_constraint
  c != null
  c != ""
  _unbounded(c)
  msg := sprintf("Provider %s/%s is constrained as '%s', which has no upper bound", [p.namespace, p.type, c])
}

_unbounded(c) if {
  t := trim_space(c)
  startswith(t, ">")
  not contains(t, "<")
}

_unbounded(c) if { trim_space(c) == "*" }`,
    },
    {
      label: 'Approved providers only (declared)',
      description: 'Restrict which providers a module may declare in required_providers',
      rego: `package terramantle.policy

approved := {"hashicorp/aws", "hashicorp/google", "hashicorp/azurerm", "hashicorp/random", "hashicorp/null"}

deny contains msg if {
  some p in input.required_providers
  not concat("/", [p.namespace, p.type]) in approved
  msg := sprintf("Module requires provider %s/%s, which is not approved", [p.namespace, p.type])
}`,
    },
    {
      label: 'Approved publishers only',
      description: 'Restrict modules to namespaces your platform team publishes or has reviewed',
      rego: `package terramantle.policy

# The namespaces you publish under, plus any vendor you have reviewed.
approved_publishers := {"my-org", "terraform-aws-modules"}

deny contains msg if {
  not input.module.namespace in approved_publishers
  msg := sprintf("Module namespace '%s' is not an approved publisher", [input.module.namespace])
}`,
    },
    {
      label: 'No deprecated versions',
      description: 'Block module versions marked deprecated in the registry',
      rego: `package terramantle.policy

deny contains msg if {
  input.module.deprecated
  msg := sprintf("Module version %s is deprecated in the registry", [input.module.version])
}`,
    },
    {
      label: 'Require a stable release',
      description: 'Block pre-1.0 and pre-release versions, which promise nothing about compatibility',
      rego: `package terramantle.policy

deny contains msg if {
  startswith(input.module.version, "0.")
  msg := sprintf("Version %s is pre-1.0 - the module makes no compatibility promise yet", [input.module.version])
}

deny contains msg if {
  contains(input.module.version, "-")
  msg := sprintf("Version %s is a pre-release, not a stable release", [input.module.version])
}`,
    },
    {
      label: 'Provider allowlist',
      description: 'Restrict modules to approved providers only',
      rego: `package terramantle.policy

allowed_providers := {"aws", "google", "azurerm"}

deny contains msg if {
  not input.module.provider in allowed_providers
  msg := sprintf("Provider '%s' is not in the approved list", [input.module.provider])
}`,
    },
  ],

  state: [
    {
      label: 'No secrets in state',
      description: 'Block state that contains hardcoded secrets (API keys, tokens, passwords)',
      baseline: true,
      popular: true,
      rego: `package terramantle.policy

deny contains msg if {
  f := input.findings[_]
  f.category == "secret"
  msg := sprintf("Secret detected in state: %s", [f.title])
}`,
    },
    {
      label: 'No live credentials in state',
      description: 'Block only the secrets the scanner proved are live - a narrower gate than "no secrets" for an estate with a backlog',
      rego: `package terramantle.policy

# "verified" means the scanner authenticated with this credential and it worked. In a state file
# that is a working key sitting in something your pipeline copies around, not a triage item.
deny contains msg if {
  some f in input.findings
  f.category == "secret"
  f.meta.verified == true
  msg := sprintf("A LIVE credential is stored in state: %s", [f.title])
}`,
    },
    {
      label: 'No critical findings in state',
      description: 'Block the next apply when the latest state has any critical-severity findings',
      rego: `package terramantle.policy

deny contains msg if {
  input.scan.findings.critical > 0
  msg := sprintf("State has %d critical finding(s)", [input.scan.findings.critical])
}`,
    },
    {
      label: 'No critical or high findings in state',
      description: 'Block the next apply on critical OR high severity findings in the latest state',
      baseline: true,
      rego: `package terramantle.policy

deny contains msg if {
  input.scan.findings.critical > 0
  msg := sprintf("State has %d critical finding(s)", [input.scan.findings.critical])
}

deny contains msg if {
  input.scan.findings.high > 0
  msg := sprintf("State has %d high finding(s)", [input.scan.findings.high])
}`,
    },
    {
      label: 'No internet-exposed resources',
      description: 'Block state showing resources reachable from the public internet (open ports, public endpoints)',
      baseline: true,
      rego: `package terramantle.policy

deny contains msg if {
  f := input.findings[_]
  f.category == "exposure"
  msg := sprintf("Internet-exposed resource in state: %s", [f.title])
}`,
    },
    {
      label: 'Require audit identity on state',
      description: 'Block a state version with no workspace or lineage, so every version can be placed in its history',
      rego: `package terramantle.policy

# Without a workspace nobody can say which environment this version describes; without a lineage
# nobody can tell an evolved state from a re-initialised one. Both are MISSING SIGNALS rather
# than bad values, so this follows the org's signal-fail gate instead of denying outright.

# Missing signal + Hard Fail gate.
deny contains msg if {
  input.settings.on_missing_signal == "hard_fail"
  input.state.workspace == null
  msg := "State version carries no workspace identity and the signal-fail gate is set to Hard Fail"
}

deny contains msg if {
  input.settings.on_missing_signal == "hard_fail"
  input.state.lineage == null
  msg := "State version carries no Terraform lineage and the signal-fail gate is set to Hard Fail"
}`,
    },
    {
      label: 'Limit resource footprint',
      description: 'Block state that manages more than a set number of resources (blast-radius guard)',
      rego: `package terramantle.policy

max_resources := 500

deny contains msg if {
  input.state.resource_count > max_resources
  msg := sprintf("State manages %d resources (limit is %d)", [input.state.resource_count, max_resources])
}`,
    },
  ],

  provider: [
    {
      label: 'Block malware',
      description: 'Block providers whose build or source was flagged as containing malware',
      baseline: true,
      popular: true,
      rego: `package terramantle.policy

deny contains msg if {
  input.provider.malware_detected == true
  msg := "Provider source flagged as containing malware"
}

deny contains msg if {
  f := input.findings[_]
  f.category == "malware"
  msg := sprintf("Malware finding in provider: %s", [f.title])
}`,
    },
    {
      label: 'No critical vulnerabilities',
      description: 'Block providers with any critical-severity CVE / vulnerability findings',
      baseline: true,
      rego: `package terramantle.policy

# The VULNERABILITY rollup, not input.scan.findings - that one counts secrets and
# misconfiguration too, and a critical secret is not a critical vulnerability.
deny contains msg if {
  input.scan.vulnerabilities.critical > 0
  msg := sprintf("Provider has %d critical vulnerability(ies)", [input.scan.vulnerabilities.critical])
}`,
    },
    {
      label: 'No high CVE without a fix',
      description: 'Block a high-severity vulnerability that has no available fix - there is nothing to upgrade to, so it needs a waiver',
      rego: `package terramantle.policy

# The rollup the scanner derives: at least one high-severity vulnerability whose meta shows no
# available fix.
deny contains msg if {
  input.scan.vulnerabilities.has_unfixable_high
  msg := "A high-severity vulnerability in this provider has no available fix"
}

# The same thing per finding, so the message names what has to be waived. fixed_version is NULL
# (not absent) when no fix exists, and "not f.meta.fixed_version" would not catch null.
deny contains msg if {
  some f in input.findings
  f.category == "vulnerability"
  f.severity == "high"
  not _fix_available(f)
  msg := sprintf("%s has no fixed version available", [f.rule_id])
}

_fix_available(f) if {
  v := f.meta.fixed_version
  v != null
  v != ""
}`,
    },
    {
      label: 'CVSS ceiling',
      description: 'Block any vulnerability scoring at or above a CVSS threshold, rather than trusting the severity band',
      rego: `package terramantle.policy

max_cvss := 9.0

deny contains msg if {
  some f in input.findings
  f.category == "vulnerability"
  f.meta.cvss_score != null
  f.meta.cvss_score >= max_cvss
  msg := sprintf("%s scores %.1f, at or above the ceiling of %.1f", [f.rule_id, f.meta.cvss_score, max_cvss])
}

# Missing signal + Hard Fail gate: a high or critical finding nobody could score.
deny contains msg if {
  input.settings.on_missing_signal == "hard_fail"
  some f in input.findings
  f.category == "vulnerability"
  f.severity in {"critical", "high"}
  f.meta.cvss_score == null
  msg := sprintf("%s carries no CVSS score and the signal-fail gate is set to Hard Fail", [f.rule_id])
}`,
    },
    {
      label: 'Block actively exploited CVEs',
      description: 'Block any CVE on the CISA Known Exploited Vulnerabilities catalogue, whatever its score',
      baseline: true,
      popular: true,
      rego: `package terramantle.policy

deny contains msg if {
  some f in input.findings
  f.meta.kev
  msg := sprintf("%s is on the CISA Known Exploited Vulnerabilities catalogue", [f.rule_id])
}

# Missing signal + Hard Fail gate.
deny contains msg if {
  input.settings.on_missing_signal == "hard_fail"
  input.scan.vulnerabilities.kev_count == null
  msg := "Exploitation data unavailable and the signal-fail gate is set to Hard Fail"
}`,
    },
    {
      label: 'Require SBOM',
      description: 'Block provider builds that did not produce a software bill of materials',
      rego: `package terramantle.policy

deny contains msg if {
  not input.provider.sbom.generated
  msg := "Provider build has no SBOM (software bill of materials)"
}

# A generated SBOM that failed to upload is an infrastructure problem, not a supply-chain one.
# Gate on it only if you mean to.
deny contains msg if {
  input.provider.sbom.generated
  not input.provider.sbom.uploaded
  msg := "Provider SBOM was generated but could not be stored"
}`,
    },
    {
      label: 'Every build traceable to a commit',
      description: 'Block a provider build that is not pinned to a source commit - nobody can say what was compiled',
      rego: `package terramantle.policy

# provenance.commit_sha is populated even when the repo's signals could not be fetched and
# provider.source is null, so it is the one to gate on rather than source.commit_sha.
deny contains msg if {
  input.provider.provenance.commit_sha == null
  msg := "Provider build is not pinned to a source commit - the tree it was built from cannot be identified"
}`,
    },
    {
      label: 'Patched builds must name their ref',
      description: 'Block a terramantle-patched build that did not record the ref it was built from',
      rego: `package terramantle.policy

# A patched build clones a private fork at an explicit ref. Without the ref recorded, the build
# cannot be reproduced or reviewed - and a patched build carries fixes upstream has not released,
# so it is the one that most needs to be.
deny contains msg if {
  input.provider.provenance.patched == true
  input.provider.provenance.source_ref == null
  msg := "Patched provider build does not record the ref it was built from"
}`,
    },
    {
      label: 'No archived or forked upstreams',
      description: 'Block providers whose source repo is archived (never fixed again) or is itself a fork',
      baseline: true,
      popular: true,
      rego: `package terramantle.policy

deny contains msg if {
  input.provider.source.archived == true
  msg := "Provider source repository is archived - it will never receive another fix"
}

deny contains msg if {
  input.provider.source.fork == true
  msg := "Provider is built from a FORK of its source repository, not the canonical one"
}`,
    },
    {
      label: 'Approved publishers only',
      description: 'Restrict providers to namespaces you have reviewed - a typosquat sits one character from a name on the list',
      rego: `package terramantle.policy

approved_publishers := {"hashicorp", "opentofu", "integrations"}

deny contains msg if {
  not input.provider.namespace in approved_publishers
  msg := sprintf("Provider namespace '%s' is not an approved publisher", [input.provider.namespace])
}`,
    },
    {
      label: 'Source on an inspectable host',
      description: 'Block providers whose source lives somewhere its trust signals cannot be read',
      rego: `package terramantle.policy

deny contains msg if {
  input.provider.source.host == "other"
  msg := "Provider source is hosted where its trust signals cannot be read (neither GitHub nor GitLab)"
}

# Missing signal + Hard Fail gate.
_host_missing if { input.provider.source == null }
_host_missing if { input.provider.source.host == null }

deny contains msg if {
  input.settings.on_missing_signal == "hard_fail"
  _host_missing
  msg := "Provider source repository could not be discovered and the signal-fail gate is set to Hard Fail"
}`,
    },
    {
      label: 'Not too far behind latest',
      description: 'Block provider versions several releases behind the newest upstream release',
      rego: `package terramantle.policy

max_versions_behind := 5

deny contains msg if {
  input.provider.registry.versions_behind != null
  input.provider.registry.versions_behind > max_versions_behind
  msg := sprintf("Provider is %d releases behind latest (limit is %d)", [input.provider.registry.versions_behind, max_versions_behind])
}`,
    },
    {
      label: 'Quarantine brand-new releases',
      description: 'Hold a release for its first week, so a compromised or typosquatted version is caught before you ship it',
      rego: `package terramantle.policy

# A compromised or typosquatted release is usually caught within days of publication. Waiting
# costs you the newest version for a week; not waiting costs you the week's news.
cooldown_days := 7

# Silent for a terramantle-patched build, which has no upstream registry entry (registry is
# null), and for a timestamp OPA cannot parse - a builtin error leaves the rule undefined.
deny contains msg if {
  input.provider.registry.published_at != null
  published := time.parse_rfc3339_ns(input.provider.registry.published_at)
  age_days := (time.now_ns() - published) / (24 * 60 * 60 * 1000000000)
  age_days < cooldown_days
  msg := sprintf("Provider was published %.1f days ago; the cooldown is %d days", [age_days, cooldown_days])
}`,
    },
    {
      label: 'No copyleft dependencies',
      description: 'Block providers that pull in a dependency under a licence you cannot ship',
      rego: `package terramantle.policy

# NOTE: these are the licences of the provider's DEPENDENCIES (from its SBOM).
# input.provider.source.license is a different thing - the repo's own licence.
banned := {"GPL-3.0-only", "GPL-3.0-or-later", "AGPL-3.0-only", "AGPL-3.0-or-later"}

deny contains msg if {
  some licence in input.provider.sbom.dependency_licenses
  licence in banned
  msg := sprintf("A dependency is licensed %s", [licence])
}`,
    },
    {
      label: 'Approved source licenses only',
      description: 'Only allow providers whose source repo uses an approved OSS license. A missing license follows the org signal-fail gate.',
      baseline: true,
      popular: true,
      rego: `package terramantle.policy

approved_licenses := {"MIT", "Apache-2.0", "BSD-2-Clause", "BSD-3-Clause", "MPL-2.0", "ISC"}

deny contains msg if {
  input.provider.source.license != null
  not approved_licenses[input.provider.source.license]
  msg := sprintf("Provider source license '%s' is not on the approved list", [input.provider.source.license])
}

# Missing signal + Hard Fail gate.
_license_missing if { input.provider.source == null }
_license_missing if { input.provider.source.license == null }

deny contains msg if {
  input.settings.on_missing_signal == "hard_fail"
  _license_missing
  msg := "Provider source license unavailable and the signal-fail gate is set to Hard Fail"
}`,
    },
    {
      label: 'Minimum community trust',
      description: 'Block providers whose source repo has weak community signal (fewer than 25 stars). A missing signal follows the org signal-fail gate.',
      rego: `package terramantle.policy

deny contains msg if {
  input.provider.source.stars != null
  input.provider.source.stars < 25
  msg := sprintf("Provider source repo has weak community signal (%d stars, under 25)", [input.provider.source.stars])
}

# Missing signal + Hard Fail gate.
_stars_missing if { input.provider.source == null }
_stars_missing if { input.provider.source.stars == null }

deny contains msg if {
  input.settings.on_missing_signal == "hard_fail"
  _stars_missing
  msg := "Provider star count unavailable and the signal-fail gate is set to Hard Fail"
}`,
    },
    {
      label: 'More than one maintainer',
      description: 'Block a single-maintainer dependency, where one compromised or departed account is the whole supply chain',
      rego: `package terramantle.policy

min_contributors := 2

deny contains msg if {
  input.provider.source.contributors != null
  input.provider.source.contributors < min_contributors
  msg := sprintf("Provider source repo has %d contributor(s) - a single-maintainer dependency", [input.provider.source.contributors])
}

# Missing signal + Hard Fail gate. ALWAYS null for GitLab, which publishes no cheap total - so
# Hard Fail here denies every GitLab-hosted provider, whatever its real contributor count.
_contributors_missing if { input.provider.source == null }
_contributors_missing if { input.provider.source.contributors == null }

deny contains msg if {
  input.settings.on_missing_signal == "hard_fail"
  _contributors_missing
  msg := "Provider contributor count unavailable and the signal-fail gate is set to Hard Fail"
}`,
    },
    {
      label: 'No abandoned sources',
      description: 'Block providers whose source repository appears dormant. A missing cadence signal follows the org signal-fail gate.',
      rego: `package terramantle.policy

deny contains msg if {
  input.provider.source.commit_cadence == "dormant"
  msg := "Provider source repository appears dormant (no recent commits)"
}

# Missing signal + Hard Fail gate.
_cadence_missing if { input.provider.source == null }
_cadence_missing if { input.provider.source.commit_cadence == null }

deny contains msg if {
  input.settings.on_missing_signal == "hard_fail"
  _cadence_missing
  msg := "Provider commit cadence unavailable and the signal-fail gate is set to Hard Fail"
}`,
    },
    {
      label: 'Supply-chain scorecard',
      description: 'Block on a CLUSTER of weak signals rather than any one of them - no single signal condemns a provider',
      rego: `package terramantle.policy

# Any one of these has an innocent explanation. Two together, less often. Raise the limit to
# make the gate more forgiving; drop it to 0 and you have seven separate policies.
max_weak_signals := 1

weak contains "source repo is archived" if { input.provider.source.archived == true }

weak contains "source repo is a fork" if { input.provider.source.fork == true }

weak contains "commit history is dormant" if { input.provider.source.commit_cadence == "dormant" }

weak contains "never released" if { input.provider.source.release_cadence == "none" }

weak contains "under 25 stars" if {
  input.provider.source.stars != null
  input.provider.source.stars < 25
}

weak contains "no SBOM" if { not input.provider.sbom.generated }

weak contains "an unfixable high-severity CVE" if { input.scan.vulnerabilities.has_unfixable_high }

deny contains msg if {
  count(weak) > max_weak_signals
  msg := sprintf("Provider shows %d weak supply-chain signals (limit is %d): %s", [count(weak), max_weak_signals, concat("; ", sort(weak))])
}`,
    },
  ],

  // Plan templates read input.plan.* ONLY, deliberately: those keys are present in BOTH
  // evaluation paths, so a plan policy written from one of these works in the synchronous gate
  // that can actually BLOCK an apply. input.findings exists only in the later scanner pass, where
  // the verdict is advisory.
  plan: [
    {
      label: 'Protect stateful resources',
      description: 'Block a plan that would destroy or replace a database or other stateful resource',
      popular: true,
      rego: `package terramantle.policy

protected := {"aws_db_instance", "aws_rds_cluster", "google_sql_database_instance", "azurerm_mssql_database"}

deny contains msg if {
  some r in input.plan.resources
  r.type in protected
  # replace destroys before it recreates, so gate on both.
  r.action in {"destroy", "replace"}
  msg := sprintf("Plan would %s %d %s", [r.action, r.count, r.type])
}`,
    },
    {
      label: 'Never destroy the audit trail',
      description: 'Block a plan that removes logging or audit infrastructure - everything after that apply is unobserved',
      popular: true,
      rego: `package terramantle.policy

audit_resources := {
  "aws_cloudtrail",
  "aws_config_configuration_recorder",
  "aws_cloudwatch_log_group",
  "aws_flow_log",
  "google_logging_project_sink",
  "google_logging_folder_sink",
  "azurerm_monitor_diagnostic_setting",
  "azurerm_log_analytics_workspace",
}

deny contains msg if {
  some r in input.plan.resources
  r.type in audit_resources
  # replace destroys before it recreates, and the gap is exactly the unobserved window.
  r.action in {"destroy", "replace"}
  msg := sprintf("Plan would %s %d %s - the audit trail has to survive the apply", [r.action, r.count, r.type])
}`,
    },
    {
      label: 'Protect the encryption keys',
      description: 'Block a plan that destroys a KMS key or vault - the data encrypted under it becomes unrecoverable',
      rego: `package terramantle.policy

key_resources := {
  "aws_kms_key",
  "aws_kms_alias",
  "google_kms_crypto_key",
  "azurerm_key_vault",
  "azurerm_key_vault_key",
}

deny contains msg if {
  some r in input.plan.resources
  r.type in key_resources
  r.action in {"destroy", "replace"}
  msg := sprintf("Plan would %s %d %s - data encrypted under it becomes unrecoverable", [r.action, r.count, r.type])
}`,
    },
    {
      label: 'Access control changes need a reviewer',
      description: 'Flag any plan touching IAM, roles or service accounts, so a privilege change is never a side effect',
      rego: `package terramantle.policy

# Matched by PREFIX, so a resource type nobody has thought of yet is covered the day it appears.
access_prefixes := {"aws_iam_", "google_project_iam", "google_service_account", "azurerm_role_"}

deny contains msg if {
  some r in input.plan.resources
  some prefix in access_prefixes
  startswith(r.type, prefix)
  r.action != "noop"
  msg := sprintf("Plan changes access control: %s %d %s", [r.action, r.count, r.type])
}`,
    },
    {
      label: 'Limit the blast radius',
      description: 'Block a plan that removes more than a set number of resources',
      rego: `package terramantle.policy

max_removed := 10

deny contains msg if {
  removed := input.plan.destroy_count + input.plan.replace_count
  removed > max_removed
  msg := sprintf("Plan removes %d resources (limit is %d)", [removed, max_removed])
}`,
    },
    {
      label: 'Block a destroy-only apply',
      description: 'Block a plan that only tears down - nothing created, nothing updated, which is rarely what anyone meant',
      rego: `package terramantle.policy

deny contains msg if {
  removed := input.plan.destroy_count + input.plan.replace_count
  removed > 0
  input.plan.add_count == 0
  input.plan.change_count == 0
  msg := sprintf("This plan only tears down: %d resource(s) removed, nothing created or updated", [removed])
}`,
    },
    {
      label: 'No sensitive-value churn',
      description: 'Block a plan that writes to a value Terraform marked sensitive, so secret material moves on its own change',
      rego: `package terramantle.policy

# A COUNT, never the values: the plan render is redacted before a policy ever sees it.
deny contains msg if {
  input.plan.sensitive_change_count > 0
  msg := sprintf("%d change(s) touch a value Terraform marked sensitive", [input.plan.sensitive_change_count])
}`,
    },
    {
      label: 'No unreviewed drift',
      description: 'Block a plan where resources have drifted outside Terraform',
      rego: `package terramantle.policy

deny contains msg if {
  input.plan.drift_count > 0
  msg := sprintf("%d resource(s) drifted outside Terraform - reconcile before applying", [input.plan.drift_count])
}`,
    },
    {
      label: 'Minimum Terraform version',
      description: 'Block a plan produced by a Terraform older than your floor',
      rego: `package terramantle.policy

minimum := "1.5.0"

deny contains msg if {
  v := input.plan.terraform_version
  v != null
  semver.is_valid(v)
  semver.compare(v, minimum) < 0
  msg := sprintf("Plan was produced by Terraform %s; %s or newer is required", [v, minimum])
}

# Missing signal + Hard Fail gate: no version in the render, or one that cannot be compared.
_version_unknown if { input.plan.terraform_version == null }

_version_unknown if {
  input.plan.terraform_version != null
  not semver.is_valid(input.plan.terraform_version)
}

deny contains msg if {
  input.settings.on_missing_signal == "hard_fail"
  _version_unknown
  msg := "Terraform version could not be determined and the signal-fail gate is set to Hard Fail"
}`,
    },
    {
      label: 'Approved providers in the plan',
      description: 'Block a plan that reaches for a provider outside the registries and namespaces you approve',
      rego: `package terramantle.policy

# Fully-qualified, as the plan reports them: registry.terraform.io/hashicorp/aws.
approved_prefixes := {"registry.terraform.io/hashicorp/", "registry.opentofu.org/opentofu/"}

deny contains msg if {
  some r in input.plan.resources
  r.provider != null
  not _approved(r.provider)
  msg := sprintf("Plan uses provider %s (for %s), which is not approved", [r.provider, r.type])
}

_approved(p) if {
  some prefix in approved_prefixes
  startswith(p, prefix)
}`,
    },
  ],
}

// ── Gallery helpers ───────────────────────────────────────────────────────────

/**
 * Templates matching a search, over label AND description.
 *
 * Terms are ANDed, so "critical module" matches a card whose label carries one word and whose
 * description carries the other - which is how someone searching actually types. An empty or
 * whitespace-only query matches everything.
 *
 * Returns a new array: the caller renders from it, and TEMPLATES is not theirs to reorder.
 */
export function filterTemplates(list: readonly Template[], query: string): Template[] {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
  if (terms.length === 0) return [...list]
  return list.filter((t) => {
    const haystack = `${t.label} ${t.description}`.toLowerCase()
    return terms.every((term) => haystack.includes(term))
  })
}

/**
 * Split the curated templates out, preserving the authored order WITHIN each group. The order in
 * TEMPLATES is editorial - related gates sit next to each other - so nothing here sorts.
 */
export function splitPopular(list: readonly Template[]): { popular: Template[]; rest: Template[] } {
  const popular: Template[] = []
  const rest: Template[] = []
  for (const t of list) (t.popular ? popular : rest).push(t)
  return { popular, rest }
}

/**
 * The recommended baseline for one artefact: the set the "Add recommended" dialog offers, in
 * authored order.
 *
 * This is the replacement for the policies that used to be seeded into every new org at creation
 * time. That seed wrote a set nobody chose, under names that matched nothing in this file, and it
 * is gone - so this list is now the only answer to "what should an org start with", and it is
 * worth keeping honest: 3-5 per artefact, each gate covering ground none of the others do.
 *
 * `plan` has no baseline today: plan verdicts do not gate yet, so recommending a set would be
 * recommending something that cannot do anything. It returns empty rather than throwing, because
 * the caller iterates artefacts.
 *
 * Returns a new array: the caller filters and sorts it, and TEMPLATES is not theirs to reorder.
 */
export function baselineTemplates(artefactType: PolicyArtefactType): Template[] {
  return (TEMPLATES[artefactType] ?? []).filter((t) => t.baseline)
}

// ── The empty-editor scaffold ─────────────────────────────────────────────────

/**
 * The first rule for each artefact: the simplest useful gate, in real Rego.
 *
 * Kept uncommented and exported so the test suite can parse and EVALUATE it the same way it does
 * the templates. The scaffold below comments it out for display; if it were only ever a string of
 * `#` lines, a broken example could ship and nobody would find out until an author uncommented it.
 */
export const FIRST_RULE: Record<PolicyArtefactType, string> = {
  module: `deny contains msg if {
  input.scan.findings.critical > 0
  msg := sprintf("Module has %d critical finding(s)", [input.scan.findings.critical])
}`,
  state: `deny contains msg if {
  input.scan.findings.critical > 0
  msg := sprintf("State has %d critical finding(s)", [input.scan.findings.critical])
}`,
  provider: `deny contains msg if {
  input.scan.vulnerabilities.critical > 0
  msg := sprintf("Provider has %d critical vulnerability(ies)", [input.scan.vulnerabilities.critical])
}`,
  plan: `deny contains msg if {
  input.plan.destroy_count > 5
  msg := sprintf("Plan destroys %d resources", [input.plan.destroy_count])
}`,
}

const ARTEFACT_NOUN: Record<PolicyArtefactType, string> = {
  module: 'module', state: 'state file', provider: 'provider', plan: 'plan',
}

/** Prefix every line with #, keeping blank lines as a bare # so the block reads as one comment. */
const commentOut = (block: string): string =>
  block.split('\n').map((line) => (line.length > 0 ? `# ${line}` : '#')).join('\n')

/**
 * What a NEW policy's editor opens with.
 *
 * An empty box is the worst possible starting point for Rego: the language inverts what most
 * people expect (you write what is FORBIDDEN, there is no allow), the rule syntax is unusual
 * enough that guessing fails, and nothing on screen says which fields exist. So the buffer opens
 * with the shape of a rule, a working example for THIS artefact, and a pointer at the two things
 * that answer "what can I read?" - the completions and the (?) reference.
 *
 * Everything except the package declaration is commented, so the scaffold is inert: an author who
 * ignores it entirely and types underneath still has a valid policy, and one who deletes the lot
 * loses nothing. The package line is left live because it is mandatory, the API rejects a policy
 * without it, and nobody new could be expected to know its exact spelling.
 */
export function newPolicyScaffold(artefact: PolicyArtefactType): string {
  const noun = ARTEFACT_NOUN[artefact]
  // The package declaration comes FIRST, and that ordering is load-bearing rather than tidy: Rego
  // requires it to open the module, so a guide that sat above it would turn "delete the # to
  // switch this rule on" into a parse error the moment anyone followed it.
  return `package terramantle.policy

${commentOut(`── Writing a policy ──

A policy is a list of DENY rules. Each rule that matches adds one message.
No messages at all = this ${noun} passes. Any message = it fails, and the
messages are the reason shown to whoever hits the block. There is no "allow".

Every rule has the same shape:

  deny contains msg if {
    <a condition>         # every line has to hold for the rule to fire
    <another condition>   # they are ANDed together
    msg := "why this failed"
  }

"input" is the ${noun} being evaluated. Type "input." below and the editor lists
every field it actually has, with a description; hover one to read it again. The
(?) button above opens the full reference.

Here is a working rule - delete the # from these lines to switch it on:

${FIRST_RULE[artefact]}

Or press "Browse templates" above to start from a worked example.`)}
`
}
